import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';

type Conversation = {
  id: string;
  title: string;
  updatedAt: string;
};

type Message = {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  toolCalls?: ToolCallCard[];
};

type ToolCallCard = {
  id: string;
  toolName: string;
  status: 'running' | 'completed' | 'failed' | 'needs_confirmation';
  result?: any;
  clientAction?: any;
  input?: any;
  description?: string;
};

const TOOL_LABELS: Record<string, string> = {
  list_campaigns: 'Looking up campaigns',
  get_campaign_details: 'Loading campaign details',
  create_campaign: 'Creating campaign',
  launch_campaign: 'Launching campaign',
  list_contacts: 'Searching contacts',
  navigate_to_page: 'Navigating',
  get_analytics_overview: 'Loading analytics',
  approve_email_draft: 'Approving email draft',
};

interface AIPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AIPanel({ isOpen, onClose }: AIPanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCallCard[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<ToolCallCard | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/ai-chat/conversations', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setConversations(data);
          if (!activeConversationId && data.length > 0) {
            setActiveConversationId(data[0].id);
          }
        }
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }
    fetch(`/api/ai-chat/conversations/${activeConversationId}/messages`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setMessages(data);
      })
      .catch(() => {});
  }, [activeConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, activeToolCalls, scrollToBottom]);

  const handleClientAction = useCallback((action: any) => {
    if (!action) return;
    if (action.type === 'navigate') {
      navigate(action.path);
    } else if (action.type === 'refresh_data') {
      mutate(action.key);
    }
  }, [navigate]);

  // Tools that mutate campaign rows — invalidate the open editor's SWR caches so
  // the user sees agent-side changes without a manual refresh.
  const CAMPAIGN_MUTATING_TOOLS = new Set([
    'create_campaign',
    'launch_campaign',
    'pause_campaign',
    'edit_campaign_settings',
    'edit_campaign_icp',
    'update_campaign',
  ]);

  const invalidateForTool = useCallback((toolName: string, input: any, result: any) => {
    if (CAMPAIGN_MUTATING_TOOLS.has(toolName)) {
      mutate('/api/campaigns');
      const id = input?.campaignId ?? input?.id ?? result?.data?.id;
      if (id) mutate(`/api/campaigns/${id}`);
    }
    if (toolName === 'approve_email_draft') {
      mutate((key: string) => typeof key === 'string' && key.startsWith('/api/campaigns/'));
    }
  }, []);

  const createConversation = async () => {
    try {
      const res = await fetch('/api/ai-chat/conversations', {
        method: 'POST',
        credentials: 'include',
      });
      const conv = await res.json();
      setConversations(prev => [conv, ...prev]);
      setActiveConversationId(conv.id);
      setMessages([]);
      setInput('');
    } catch {}
  };

  const sendMessage = async () => {
    const content = input.trim();
    if (!content || isStreaming) return;

    let conversationId = activeConversationId;

    if (!conversationId) {
      try {
        const res = await fetch('/api/ai-chat/conversations', {
          method: 'POST',
          credentials: 'include',
        });
        const conv = await res.json();
        setConversations(prev => [conv, ...prev]);
        setActiveConversationId(conv.id);
        conversationId = conv.id;
      } catch {
        return;
      }
    }

    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setInput('');
    setIsStreaming(true);
    setStreamingText('');
    setActiveToolCalls([]);
    setPendingConfirmation(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const res = await fetch(`/api/ai-chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          uiContext: { currentPage: location.pathname },
        }),
      });

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      const toolCalls: ToolCallCard[] = [];

      if (reader) {
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'token') {
                fullText += data.text;
                setStreamingText(fullText);

              } else if (data.type === 'tool_start') {
                const tc: ToolCallCard = {
                  id: data.toolCallId,
                  toolName: data.toolName,
                  status: 'running',
                  input: data.input,
                };
                toolCalls.push(tc);
                setActiveToolCalls([...toolCalls]);

              } else if (data.type === 'tool_result') {
                const idx = toolCalls.findIndex(t => t.id === data.toolCallId);
                if (idx >= 0) {
                  toolCalls[idx] = {
                    ...toolCalls[idx],
                    status: data.result?.success ? 'completed' : 'failed',
                    result: data.result,
                    clientAction: data.clientAction,
                  };
                  setActiveToolCalls([...toolCalls]);
                }
                if (data.result?.success) {
                  invalidateForTool(toolCalls[idx]?.toolName ?? '', toolCalls[idx]?.input, data.result);
                }
                if (data.clientAction) {
                  handleClientAction(data.clientAction);
                }

              } else if (data.type === 'tool_needs_confirmation') {
                const tc: ToolCallCard = {
                  id: data.toolCallId,
                  toolName: data.toolName,
                  status: 'needs_confirmation',
                  input: data.input,
                  description: data.description,
                };
                toolCalls.push(tc);
                setActiveToolCalls([...toolCalls]);
                setPendingConfirmation(tc);

              } else if (data.type === 'done') {
                setMessages(prev => [...prev, {
                  id: data.messageId,
                  role: 'assistant',
                  content: fullText,
                  createdAt: new Date().toISOString(),
                  toolCalls: toolCalls.length > 0 ? [...toolCalls] : undefined,
                }]);
                setStreamingText('');
                setActiveToolCalls([]);
                if (data.title) {
                  setConversations(prev => prev.map(c =>
                    c.id === conversationId ? { ...c, title: data.title } : c
                  ));
                }

              } else if (data.type === 'error') {
                setStreamingText('');
              }
            } catch {}
          }
        }
      }
    } catch {
      setStreamingText('');
    }

    setIsStreaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderToolCalls = (calls: ToolCallCard[]) => (
    <div className="space-y-1.5 my-2">
      {calls.map(tc => (
        <div key={tc.id} className="flex items-center gap-2 text-xs text-muted-foreground">
          {tc.status === 'running' && (
            <span className="inline-block w-3 h-3 border-2 border-info-on-dark border-t-transparent rounded-full animate-spin" />
          )}
          {tc.status === 'completed' && (
            <span className="inline-flex items-center justify-center w-4 h-4 bg-success-surface-strong rounded-full">
              <CheckIcon className="w-2.5 h-2.5 text-success-foreground" />
            </span>
          )}
          {tc.status === 'failed' && (
            <XCircleIcon className="h-3.5 w-3.5 text-danger-text" />
          )}
          {tc.status === 'needs_confirmation' && (
            <span className="inline-block w-3 h-3 bg-warning-solid-soft rounded-full" />
          )}
          <span>{TOOL_LABELS[tc.toolName] || tc.toolName}</span>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="h-28 bg-gradient-to-b from-card via-card/80 to-card/0 absolute top-0 left-0 right-[8px] z-[9] pointer-events-none" />
      <div className="h-56 bg-gradient-to-b from-card/0 to-card absolute bottom-0 left-0 right-[8px] z-[8] pointer-events-none" />

      {/* Header */}
      <div className="absolute top-5 left-5 right-5 z-50 flex items-center gap-3">
        <Button variant="secondary" onClick={createConversation}>
          New
        </Button>
        {conversations.length > 0 ? (
          <Select
            value={activeConversationId || undefined}
            onValueChange={(id) => setActiveConversationId(id)}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select conversation" />
            </SelectTrigger>
            <SelectContent>
              {conversations.map(conv => (
                <SelectItem key={conv.id} value={conv.id}>
                  {conv.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex-1" />
        )}
        <DialogCloseButton onClick={onClose} className="ml-3" />
      </div>

      {/* Input area */}
      {(() => {
        const isEmpty = messages.length === 0 && !streamingText && !isStreaming;
        return (
          <div className={`absolute left-5 right-5 z-50 transition-all duration-300 ${isEmpty ? 'top-1/2 -translate-y-1/2' : 'bottom-4'}`}>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder="How can I help you today?"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  const lineCount = (e.target.value.match(/\n/g) || []).length + 1;
                  const baseHeight = 120;
                  const lineHeight = 24;
                  const maxHeight = 300;
                  const newHeight = Math.max(baseHeight, Math.min(maxHeight, baseHeight + (lineCount - 1) * lineHeight));
                  e.target.style.height = newHeight + 'px';
                }}
                onKeyDown={handleKeyDown}
                className="min-h-[120px] resize-none bg-card shadow-xl pr-20 overflow-y-auto"
                disabled={isStreaming}
              />
              {input.trim() && !isStreaming && (
                <Button
                  variant="primary"
                  className="absolute bottom-2 right-2"
                  onClick={sendMessage}
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Messages area */}
      <div className="h-full overflow-y-auto px-7 pt-24 pb-52 space-y-7 [&::-webkit-scrollbar-track]:mt-6 [&::-webkit-scrollbar-track]:mb-6">
        {messages.map(msg => (
          <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
            <div className={`${
              msg.role === 'user'
                ? 'bg-surface-subtle text-foreground rounded-3xl rounded-tr-lg'
                : 'rounded-3xl rounded-tl-lg'
            } px-3.5 py-2.5 text-sm max-w-[85%]`}>
              {msg.toolCalls && msg.toolCalls.length > 0 && renderToolCalls(msg.toolCalls)}
              <div className="text-foreground whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}

        {/* Streaming response: tool calls + text in one bubble */}
        {(activeToolCalls.length > 0 || streamingText) && (
          <div className="flex gap-2">
            <div className="rounded-3xl rounded-tl-lg px-3.5 py-2.5 text-sm max-w-[85%]">
              {activeToolCalls.length > 0 && renderToolCalls(activeToolCalls)}
              {streamingText && (
                <div className="text-foreground whitespace-pre-wrap">
                  {streamingText}
                  <span className="inline-block w-1.5 h-4 bg-foreground/60 ml-0.5 animate-pulse" />
                </div>
              )}
              {!streamingText && activeToolCalls.length > 0 && activeToolCalls.some(t => t.status === 'running') && (
                <span className="inline-block w-1.5 h-4 bg-foreground/60 ml-0.5 animate-pulse" />
              )}
            </div>
          </div>
        )}

        {/* Confirmation card */}
        {pendingConfirmation && (
          <div className="flex gap-2">
            <div className="rounded-2xl border border-warning-border bg-warning-surface px-4 py-3 text-sm max-w-[85%]">
              <p className="font-medium text-warning-heading mb-1">
                {TOOL_LABELS[pendingConfirmation.toolName] || pendingConfirmation.toolName}
              </p>
              <p className="text-warning-foreground-deep text-xs mb-3">
                {pendingConfirmation.description || 'This action requires your confirmation.'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setPendingConfirmation(null);
                    setInput('Yes, go ahead and do it.');
                    setTimeout(() => sendMessage(), 100);
                  }}
                >
                  Confirm
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPendingConfirmation(null);
                    setInput('No, cancel that.');
                    setTimeout(() => sendMessage(), 100);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
}
